/**
 * queue.ts — durable telemetry event queue.
 *
 * Backed by idb-keyval (same pattern as serviceQueue.ts). Events survive page
 * reload and are flushed only after sign-in, over an authenticated connection.
 *
 * Contract (§6, §10):
 *   - Events queued pre-auth (app_opened, signed_in) are held here and flushed
 *     after the user authenticates.
 *   - Original `ts` is preserved through queue → flush unchanged.
 *   - Flush chunks to 100-event / 64 KiB limits (§3 caps).
 *   - All responses and errors are swallowed — telemetry must NEVER affect app
 *     behavior.
 *   - Queue is cleared on sign-out (called from AuthProvider) to prevent leaks
 *     across users on a shared device.
 *
 * IndexedDB is used (not localStorage) because:
 *   1. Sensitive data must never go to localStorage (CAPACITOR_READINESS rule 6).
 *   2. idb-keyval is already a project dependency (serviceQueue.ts uses it).
 *
 * Note: the queue stores raw TelemetryEvent objects; the batch envelope
 * (sessionId, appVersion, platform, sentAt) is assembled at flush time.
 */

import { auth } from '@/lib/firebase';
import { get, set } from 'idb-keyval';
import { getSessionId } from './session';
import type { TelemetryBatch, TelemetryEvent } from './types';

const IDB_KEY = 'gg.telemetry.v1';
const MAX_QUEUE = 500; // generous; flush drains well before this
const FLUSH_MAX_EVENTS = 100; // §3 cap per batch
const FLUSH_MAX_BYTES = 64 * 1024; // §3 cap: 64 KiB

// ---------------------------------------------------------------------------
// App version — read from import.meta.env (set by Vite from package.json)
// ---------------------------------------------------------------------------

function getAppVersion(): string {
  // Vite exposes the version from package.json if referenced via
  // import.meta.env.VITE_APP_VERSION. Fall back to the build SHA if that
  // isn't set, or 'unknown' in test.
  const v =
    (import.meta.env?.PACKAGE_VERSION as string | undefined) ??
    (import.meta.env?.VITE_BUILD_SHA as string | undefined) ??
    'unknown';
  return v;
}

// ---------------------------------------------------------------------------
// IDB read/write helpers
// ---------------------------------------------------------------------------

async function readQueue(): Promise<TelemetryEvent[]> {
  try {
    return (await get<TelemetryEvent[]>(IDB_KEY)) ?? [];
  } catch {
    return [];
  }
}

async function writeQueue(events: TelemetryEvent[]): Promise<void> {
  try {
    await set(IDB_KEY, events);
  } catch {
    // If IDB is unavailable (private browsing on some browsers), silently drop.
    // Telemetry must never affect app behavior.
  }
}

// ---------------------------------------------------------------------------
// Enqueue
// ---------------------------------------------------------------------------

/**
 * Append a telemetry event to the durable queue.
 * No-ops silently if IDB is unavailable.
 */
export async function enqueueEvent(event: TelemetryEvent): Promise<void> {
  try {
    const current = await readQueue();
    const next = [...current, event];
    // Cap: keep the newest MAX_QUEUE items to bound storage.
    const capped = next.length > MAX_QUEUE ? next.slice(next.length - MAX_QUEUE) : next;
    await writeQueue(capped);
  } catch {
    // Swallow — telemetry must never throw into the call site.
  }
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

let _flushInFlight = false;

/**
 * Flush all queued events over the authenticated connection.
 *
 * Called after sign-in (AuthProvider). No-ops if:
 *   - No events queued
 *   - User is not authenticated (no token)
 *   - A flush is already in flight
 *
 * Chunks to FLUSH_MAX_EVENTS / FLUSH_MAX_BYTES. Swallows all errors.
 */
export async function flushQueue(): Promise<void> {
  if (_flushInFlight) return;
  _flushInFlight = true;

  try {
    const user = auth.currentUser;
    if (!user) return;

    const all = await readQueue();
    if (all.length === 0) return;

    // Get token once for all chunks.
    let token: string;
    try {
      token = await user.getIdToken();
    } catch {
      return; // no valid token — leave queue intact for next flush
    }

    const appVersion = getAppVersion();
    const sessionId = getSessionId();
    const apiBase =
      (import.meta.env?.VITE_GG_API_BASE_URL as string | undefined) ??
      'https://api.garageghost.app';

    // Drain events in chunks, removing each chunk from IDB only after it's sent.
    let remaining = [...all];

    while (remaining.length > 0) {
      const chunk = buildChunk(remaining, FLUSH_MAX_EVENTS, FLUSH_MAX_BYTES);
      if (chunk.length === 0) break; // single event is over 64 KiB — drop it

      remaining = remaining.slice(chunk.length);

      const batch: TelemetryBatch = {
        sessionId,
        appVersion,
        platform: 'web',
        sentAt: new Date().toISOString(),
        events: chunk,
      };

      try {
        await fetch(`${apiBase}/v1/telemetry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(batch),
        });
        // Response is intentionally not read — §10: swallow all responses.
      } catch {
        // Network failure — leave remaining events in IDB for next flush.
        await writeQueue([...chunk, ...remaining]);
        return;
      }

      // Successfully sent — trim IDB.
      await writeQueue(remaining);
    }
  } catch {
    // Swallow all errors — telemetry must never affect app behavior.
  } finally {
    _flushInFlight = false;
  }
}

/**
 * Clear the entire queue. Called on sign-out to prevent cross-user event leaks
 * on shared devices. Mirrors serviceQueue.clearAll().
 */
export async function clearQueue(): Promise<void> {
  try {
    await writeQueue([]);
  } catch {
    // Swallow.
  }
}

// ---------------------------------------------------------------------------
// Chunk builder — respects both event-count and byte-size caps.
// ---------------------------------------------------------------------------

/**
 * Returns the largest prefix of `events` that fits within maxEvents and
 * maxBytes when JSON-serialised. If the very first event is already over
 * maxBytes, returns an empty array (that event is silently dropped by the
 * caller advancing `remaining.slice(1)`).
 */
function buildChunk(
  events: TelemetryEvent[],
  maxEvents: number,
  maxBytes: number,
): TelemetryEvent[] {
  const chunk: TelemetryEvent[] = [];
  let approxBytes = 0; // rough JSON byte count

  for (const event of events) {
    if (chunk.length >= maxEvents) break;
    const serialised = JSON.stringify(event);
    const eventBytes = serialised.length; // UTF-16 code units, close enough for ASCII payloads
    if (approxBytes + eventBytes > maxBytes) {
      if (chunk.length === 0) {
        // Single oversized event — drop it by returning empty so caller skips.
        return [];
      }
      break;
    }
    chunk.push(event);
    approxBytes += eventBytes;
  }

  return chunk;
}
